import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SERVICES, setClientService, type ServiceField } from '../../lib/clientServices';
import { Badge, Card, Dropdown, Input, Switch, Table, useToast } from '../../components/ui';
import type { BadgeTone, TableColumn } from '../../components/ui';
import useMediaQuery, { PHONE_QUERY } from '../../components/clients/useMediaQuery';

interface ServiceRow {
  id: string;
  name: string;
  status: string | null;
  service_google_ads: boolean;
  service_seo: boolean;
}

const STATUS_TONE: Record<string, BadgeTone> = { Active: 'up', Prospect: 'info', Paused: 'warn', Archived: 'idle' };

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
// The table sits inside the panel card, so it drops its own card chrome.
const flatTable: React.CSSProperties = { background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', minWidth: '560px' };
// Phones: no Status column and no min width, so the toggles are on screen without scrolling.
const phoneTable: React.CSSProperties = { ...flatTable, minWidth: 0 };

/**
 * Settings → Services: every client, with a toggle per service it buys. The flags are
 * columns on `clients` (service_*), so reports and agents can filter on them directly.
 */
export default function ServicesPanel() {
  const toast = useToast();
  const phone = useMediaQuery(PHONE_QUERY);
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  // `${clientId}:${field}` for every toggle with a write in flight.
  const [saving, setSaving] = useState<Set<string>>(new Set());

  const fetchRows = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('id, name, status, service_google_ads, service_seo')
        .order('name', { ascending: true });
      if (fetchError) throw fetchError;
      setRows(data ?? []);
    } catch (err) {
      console.error('Error fetching client services:', err);
      setError('Failed to load clients');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const toggle = async (row: ServiceRow, field: ServiceField, next: boolean) => {
    const key = `${row.id}:${field}`;
    const apply = (value: boolean) => setRows((list) => list.map((r) => (r.id === row.id ? { ...r, [field]: value } : r)));
    apply(next);
    setSaving((s) => new Set(s).add(key));
    try {
      await setClientService(row.id, field, next);
    } catch (err) {
      console.error('Error updating client service:', err);
      apply(!next);
      toast.error(`Couldn’t update ${row.name}`, 'The change was not saved.');
    }
    setSaving((s) => {
      const rest = new Set(s);
      rest.delete(key);
      return rest;
    });
  };

  const statuses = useMemo(() => [...new Set(rows.map((r) => r.status).filter((s): s is string => Boolean(s)))].sort(), [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (serviceFilter === 'none' && (r.service_google_ads || r.service_seo)) return false;
      if ((serviceFilter === 'service_google_ads' || serviceFilter === 'service_seo') && !r[serviceFilter]) return false;
      return !term || r.name.toLowerCase().includes(term);
    });
  }, [rows, search, statusFilter, serviceFilter]);

  const columns: TableColumn<ServiceRow>[] = [
    {
      key: 'name',
      label: 'Client',
      width: phone ? '50%' : '46%',
      wrap: phone,
      render: (_v, row) => (
        <Link
          to={`/clients/${row.id}`}
          className="hover-underline"
          style={{ color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], textDecoration: 'none' }}
        >
          {row.name}
        </Link>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '18%',
      render: (v) => (v ? <Badge tone={STATUS_TONE[String(v)] ?? 'neutral'} dot>{String(v)}</Badge> : <span style={faint}>—</span>),
    },
    ...SERVICES.map(
      ({ field, label }): TableColumn<ServiceRow> => ({
        key: field,
        // "Google Ads" runs into the next header at a quarter of a phone's width
        label: phone ? label.replace(/^Google /, '') : label,
        width: phone ? '25%' : '18%',
        render: (_v, row) => (
          <Switch checked={row[field]} disabled={saving.has(`${row.id}:${field}`)} onChange={(next) => toggle(row, field, next)} />
        ),
      })
    ),
  ];

  const visibleColumns = phone ? columns.filter((c) => c.key !== 'status') : columns;

  // Desktop: in the card header. Phones: full-width rows at the top of the body.
  const filters = (
    <div
      className="w-full md:w-auto"
      style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}
    >
      <div className="w-full md:w-[200px]">
        <Input icon="search" fieldSize="sm" placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="flex-1 md:flex-none md:w-[140px]">
        <Dropdown
          size="sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[{ value: 'all', label: 'Any status' }, ...statuses.map((s) => ({ value: s, label: s }))]}
        />
      </div>
      <div className="flex-1 md:flex-none md:w-[150px]">
        <Dropdown
          size="sm"
          value={serviceFilter}
          onChange={(e) => setServiceFilter(e.target.value)}
          options={[
            { value: 'all', label: 'Any services' },
            ...SERVICES.map((s) => ({ value: s.field, label: s.label })),
            { value: 'none', label: 'No services' },
          ]}
        />
      </div>
    </div>
  );

  const count = filtered.length === rows.length ? `${rows.length} clients` : `${filtered.length} of ${rows.length} clients`;

  return (
    <Card
      eyebrow="Services"
      title={isLoading && rows.length === 0 ? 'Loading...' : count}
      actions={phone ? undefined : filters}
    >
      {phone ? <div style={{ marginBottom: 'var(--space-4)' }}>{filters}</div> : null}
      {error ? <div style={{ color: 'var(--danger-400)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)' }}>{error}</div> : null}

      {rows.length === 0 ? (
        isLoading ? null : (
          <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>No clients yet.</div>
        )
      ) : (
        <>
          <div className="t-num" style={{ display: 'flex', gap: '6px var(--space-5)', flexWrap: 'wrap', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-3)' }}>
            {SERVICES.map(({ field, label }) => (
              <span key={field} style={{ whiteSpace: 'nowrap' }}>
                <span style={faint}>{label} </span>
                <span style={{ color: 'var(--text-body)' }}>{rows.filter((r) => r[field]).length}</span>
              </span>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Nothing matches those filters.</div>
          ) : (
            <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-2))' }}>
              <Table columns={visibleColumns} rows={filtered} style={phone ? phoneTable : flatTable} dense={phone} />
            </div>
          )}
        </>
      )}
    </Card>
  );
}
